import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Staff Engagement", href: "/staff-engagement" },
    { name: "Training", href: "/training" },
    { name: "HSE", href: "/health-safety" },
    { name: "Recruitment", href: "/recruitment" },
    { name: "Case Studies", href: "/case-studies" },
    { name: "Your Comms", href: "/your-comms" },
    { name: "Funding", href: "/funding" },
    { name: "Why Us", href: "/why-us" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-[#050505]/95 backdrop-blur-md border-b border-white/5"
            : "bg-gradient-to-b from-black/80 to-transparent"
        }`}
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/oukn6zco_ICA%20LOGO%20TRANSPARENT%20LOW%20RES.png" 
                alt="Internal Comms Agency" 
                className="h-14 md:h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-sm font-medium transition-colors hover:text-[#00E5FF] ${
                    location.pathname === link.href ? "text-[#00E5FF]" : "text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              <Link to="/contact/book">
                <Button
                  data-testid="nav-book-call-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-6 py-2 uppercase tracking-wider text-sm"
                >
                  Book a Call
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className="xl:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 bg-[#111111] z-40 xl:hidden pt-20"
        >
          <div className="flex flex-col items-center justify-center h-full gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-xl font-medium text-white hover:text-[#00E5FF] transition-colors"
              >
                {link.name}
              </Link>
            ))}

            <Link to="/contact/book" className="mt-4">
              <Button
                data-testid="mobile-book-call-btn"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-3 uppercase tracking-wider"
              >
                Book a Call
              </Button>
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
